import { useQuery } from '@apollo/react-hooks';
import { Queries } from 'gql';
import { LoggerFactory } from 'logger';
import React, { useCallback, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import DocumentTitle from 'react-document-title';
import qs from 'qs';
import { withRouter } from 'react-router-dom';
import Page from 'utils/Page';
import { toast } from 'utils/Toast';
import { useMarketplaceSegments, withMarketplaceProvider } from 'providers/MarketplaceProvider';
import { useCurrentProject, withLiteProjectProvider } from 'providers/LiteProjectProvider';
import LiteProjectDetailViewHeader from 'views/components/Header/LiteProjectDetailViewHeader';
import { defaultCapacityConfiguration, defaultLayerConfiguration, useProjectMapState } from 'views/ProjectDetailView/useProjectMapState';
import LitePanel from 'views/components/PhasePanel/LitePanel';
import ProjectMap from 'views/components/ProjectMap';
import UserView from './UserView';
import './ProjectDetailView/ProjectDetailView.scss';
import './ProjectDetailView/Sidebar.scss';
import './LiteView/LiteView.scss';
import './LiteCallbackView.scss';

const LiteCallbackContent = ({ user }) => {
  const { projectId, project, phase, phaseLoading, projectLoading, isEditing, changePhase } = useCurrentProject();
  const { marketplaceSegments } = useMarketplaceSegments();
  const { data: metrosData, loading: metrosLoading, error: metrosError } = useQuery(Queries.GET_LITE_METROS);
  const { data: onRampsData } = useQuery(Queries.GET_ON_RAMP_CLOUDS);
  const { data: layersData } = useQuery(Queries.GET_LAYERS_CONFIGURATION, { variables: { phaseId: phase?.id } });
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [platform] = useState(null);
  const [platformMetros] = useState(null);
  const [product] = useState(null);
  const [isSidebarCollapsed] = useState(false);
  const [zoom] = useState();
  const [sectionMapFocus, setSectionMapFocus] = useState(null);
  const {
    metros,
    connectionType,
    setConnectionType,
    infraComponentCard,
    setInfraComponentCard,
    ringLatency,
    setRingLatency,
    ringColor,
    setRingColor,
    availableRegion,
    setAvailableRegion,
    metroType,
    setMetroType,
    cloudsList,
    onRamps,
    layers,
    onRampConfiguration,
    setOnRampConfiguration,
    capacityConfiguration,
    setCapacityConfiguration,
    mapUrl,
    setMapUrl,
    selectedMapStyle,
    setSelectedMapStyle,
    selectedOnRamps,
    setSelectedOnRamps,
    openMapMenu,
    setOpenMapMenu,
    mapType,
    setMapType,
    selectedLayer,
    setSelectedLayer,
    capOnRamp,
    setCapOnRamp,
    marketplaceSegment,
    selectedEms,
    setSelectedEms,
    toggleLayer,
    toggleAvailableRegions,
    toggleLabel,
  } = useProjectMapState({ metrosData, onRampsData, layersData, marketplaceSegments });

  const loading = useMemo(() => projectLoading || metrosLoading || !phase || !project, [projectLoading, metrosLoading, project, phase]);
  const error = useMemo(() => !loading && metrosError, [loading, metrosError]);
  const onRampsMetros = onRamps?.filter((onRamp) => selectedOnRamps?.some((selected) => selected === onRamp.cloud));

  const _onSectionMapFocusChange = useCallback((focus) => {
    setSectionMapFocus((prev) => {
      if (prev?.sourceKey === focus?.sourceKey) {
        return prev;
      }
      return focus;
    });
  }, []);

  const projectCreatedOn = useMemo(() => project?.createdOn?.toString('MMMM YYYY'), [project]);

  if (error) {
    return (
      <DocumentTitle title={Page.title('Error! | Projects')}>
        <p style={{ margin: '100px' }}>ERROR! {error.message}</p>
      </DocumentTitle>
    );
  }

  if (loading) {
    return (
      <DocumentTitle title={Page.title('(Loading) | Projects')}>
        <div className="loading-container">
          <span className="loading-text">
            <div className="spinner"></div>
            Loading Project
          </span>
        </div>
      </DocumentTitle>
    );
  }

  if (showLandingPage) {
    return (
      <DocumentTitle title={Page.title(`${project.name} | Projects`)}>
        <div className="content-login-view lite-view initial lite-project-intro-view">
          <div className="lite-shell">
            <main className="lite-body lite-project-intro-view__body">
              <div className="lite-content">
                <section className="lite-project-intro-card">
                  <h1>Connectivity Advisor</h1>
                  <div className="intro-content">
                    <div>
                      <p className="lite-project-intro-card__project-name">{project.name}</p>
                      {projectCreatedOn ? <p className="lite-project-intro-card__meta">{projectCreatedOn}</p> : null}
                    </div>
                    <button type="button" className="lite-project-intro-card__cta" onClick={() => setShowLandingPage(false)}>
                      Continue →
                    </button>
                  </div>
                </section>
              </div>
            </main>
          </div>
        </div>
      </DocumentTitle>
    );
  }

  return (
    <DocumentTitle title={Page.title(`${project.name} | Projects`)}>
      <DndProvider backend={Backend}>
        <div className="app">
          <LiteProjectDetailViewHeader user={user} onChangePhase={changePhase} onReorderPhases={() => {}} onOpenPhasesModals={() => {}} />

          <div className="right-sidebar">
            <LitePanel onRampsMetros={onRampsMetros} metros={metros} onMapFocusChange={_onSectionMapFocusChange} marketplaceSegment={marketplaceSegment} />
          </div>

          <ProjectMap
            phaseId={phase.id}
            projectId={projectId}
            project={project}
            phase={phase}
            metros={metros}
            isInfoOpen={false}
            onSelect={() => {}}
            onEnableRegion={() => {}}
            onDisableRegion={() => {}}
            onManageMetros={() => {}}
            selected={null}
            selectedRegions={[]}
            connectionType={connectionType}
            setConnectionType={setConnectionType}
            setInfraComponentCard={setInfraComponentCard}
            infraComponentCard={infraComponentCard}
            layers={layers}
            toggleLayer={toggleLayer}
            ringLatency={ringLatency}
            setRingLatency={setRingLatency}
            ringColor={ringColor}
            setRingColor={setRingColor}
            availableRegion={availableRegion}
            onMetroPanelTabChange={() => {}}
            setAvailableRegion={setAvailableRegion}
            metroType={metroType}
            setMetroType={setMetroType}
            toggleAvailableRegions={toggleAvailableRegions}
            platform={platform}
            platformMetros={platformMetros}
            product={product}
            onClose={() => {}}
            isSidebarCollapsed={isSidebarCollapsed}
            openMapMenu={openMapMenu}
            changeOpenMapMenu={setOpenMapMenu}
            mapType={mapType}
            capOnRamp={capOnRamp}
            setCapOnRamp={setCapOnRamp}
            marketplaceSegment={marketplaceSegment}
            onChangeMapType={setMapType}
            onChangeMarketplaceSegment={() => {}}
            isEditing={isEditing}
            isLite
            zoom={zoom}
            toggleLabel={toggleLabel}
            cloudsList={cloudsList}
            onRamps={onRamps}
            onRampConfiguration={onRampConfiguration}
            capacityConfiguration={capacityConfiguration}
            selectedOnRamps={selectedOnRamps}
            setOnRampConfiguration={setOnRampConfiguration}
            setCapacityConfiguration={setCapacityConfiguration}
            setSelectedOnRamps={setSelectedOnRamps}
            isCapacityAllowed={false}
            selectedLayer={selectedLayer}
            setSelectedLayer={setSelectedLayer}
            mapUrl={mapUrl}
            setMapUrl={setMapUrl}
            selectedMapStyle={selectedMapStyle}
            setSelectedMapStyle={setSelectedMapStyle}
            defaultLayerConfiguration={defaultLayerConfiguration}
            defaultCapacityConfiguration={defaultCapacityConfiguration}
            selectedEms={selectedEms}
            setSelectedEms={setSelectedEms}
            sectionMapFocus={sectionMapFocus}
            loading={phaseLoading}
          />
        </div>
      </DndProvider>
    </DocumentTitle>
  );
};

const LiteCallbackContentWithProviders = withLiteProjectProvider(withMarketplaceProvider(LiteCallbackContent));

class LiteCallbackView extends UserView {
  constructor(props) {
    super(props);
    this._logger = LoggerFactory.getLogger(this);
  }

  componentDidMount() {
    // If user is already authenticated, render lite project directly in this view.
    if (this.isAuthenticated) {
      const { projectId } = this.props.match.params;
      this._logger.debug(`User authenticated, redirecting to /connectivity-advisor/${projectId}`);
      // this.props.history.replace(`/connectivity-advisor/${projectId}`);
      return;
    }

    if (this.state.errors.length > 0) {
      this._logger.warn('Errors exist, redirecting to /');
      this.props.history.replace('/');
      return;
    }

    //parse the query string
    const q = qs.parse(this.props.location.search.substring(1));

    //if the login verification failed, we will get ?error=invalid_code
    if (q.error) {
      this._logger.error(`There was an error logging in: ${q.error}`);
      toast('Authorization code is not valid or has expired', 'error');
      this.props.history.replace('/');
      return;
    }

    this._logger.debug(`Callback received from lite access service`);

    if (!q.authorization_code) {
      this._logger.warn('No authorization code received, redirecting to /');
      this.props.history.replace('/');
      return;
    }

    this.login(q.authorization_code);
  }

  render() {
    if (this.isAuthenticated) {
      return <LiteCallbackContentWithProviders user={this.user} />;
    }

    return null;
  }
}

export default withRouter(LiteCallbackView);
